// Потолок длины текста запроса. SMY-619 · 27.10.2026
//
// До этого дня текст запуска, разбора задачи и правки результата ограничивал
// только `bodyLimit` Fastify — мегабайт. Мегабайт проходил все проверки до
// модели (запрет, маскирование, признаки карты), ложился в ленту и уходил
// модели и в эмбеддинг: токены за мегабайт и отказ поставщика по длине
// контекста. Решение владельца 27.10.2026 (вариант А): 20 000 знаков — около
// десяти страниц, а документ длиннее прикладывается файлом.
//
// ОТКАЗ, А НЕ ОБРЕЗКА — тот же порядок, что у ответа на уточнение
// (`routes/clarifications.ts`): молча обрезанный запрос — это задача, сделанная
// не про то. Отвергнуто: `maxLength` у поля, как у жалобы в профиле, —
// вставленный текст обрезается молча, и человек не узнаёт, что потерял конец;
// `bodyLimit` маршрута — 413 без слов человеку и счёт в байтах, а кириллица
// занимает вдвое больше.
//
// ОДНО ПРАВИЛО НА СЕРВЕР И ОБА КЛИЕНТА: клиент закрывает отправку по той же
// проверке, по которой сервер отвечает 400, — иначе «клиент пустил, сервер
// отказал» (шапка `password.ts`). Знаки — единицы UTF-16 (`length`), как у
// всех пределов ядра.

import { groupDigits as digits } from './format.ts'
import { pluralize, type UnitLabel } from './units.ts'

/** Предел текста запроса в знаках. */
export const REQUEST_MAX_CHARS = 20_000

/** С какой длины поле начинает показывать счёт: раньше он стал бы фоном. */
const NOTE_FROM = Math.floor(REQUEST_MAX_CHARS * 0.8)

const SIGN: UnitLabel = { one: 'знак', few: 'знака', many: 'знаков' }

/** Длиннее ли текст предела. Текст — уже без пробелов по краям, как его читает ручка. */
export const requestTooLong = (text: string): boolean => text.length > REQUEST_MAX_CHARS

/** Ответ ручки за пределом. Текст человека в нём не повторяется. */
export const REQUEST_TOO_LONG = {
  run: `Запрос длиннее ${digits(REQUEST_MAX_CHARS)} знаков — сократи его и отправь снова.`,
  edit: `Правка длиннее ${digits(REQUEST_MAX_CHARS)} знаков — сократи её и отправь снова.`,
} as const

/**
 * Что объявить программе чтения с экрана, когда отправка закрылась. Без числа:
 * строка у поля меняется на каждом знаке, и объявление с числом перебивало бы
 * эхо ввода на каждом нажатии (ворота SMY-619, ux-states-checker).
 */
export const REQUEST_SEND_CLOSED = `Запрос длиннее ${digits(REQUEST_MAX_CHARS)} знаков — отправка закрыта, сократи его.`

/**
 * Строка у поля ввода о длине запроса.
 *
 * Молчит, пока до предела далеко — та же отсечка в 80 %, что у
 * `imagePromptNote`. Сверх предела отправка закрыта (`requestTooLong`), и
 * строка говорит, что делать вместо неё.
 *
 * `addition` — длина набранного, когда оно добавка к вопросу отвеченных
 * карточек (`fieldRequest` в `clarify.ts`), в той форме, что приклеится. Ворота
 * e2e SMY-619, 27.10.2026: при 10 набранных знаках строка «В запросе 20 002
 * знака… Сократи его» велела сократить то, чего в поле нет, — вопрос ушёл
 * раньше. Поэтому строка называет склейку и считает, на сколько сократить
 * именно добавку. Совет «приложить файлом» при добавке не к месту: вложение
 * снимает карточки вместе с ответами (SMY-628). Одна функция на оба случая:
 * порог и форма счёта у них общие (ворота SMY-619, dedup-hunter).
 */
export function requestLengthNote(length: number, addition: number | null = null): string | null {
  if (length <= NOTE_FROM) return null
  const count = `${digits(length)} из ${digits(REQUEST_MAX_CHARS)} знаков.`
  if (addition == null) {
    if (length <= REQUEST_MAX_CHARS) return count
    return `В запросе ${digits(length)} ${pluralize(length, SIGN)}, а уходит не больше ${digits(REQUEST_MAX_CHARS)}. Сократи его — длинный документ лучше приложить файлом.`
  }
  if (length <= REQUEST_MAX_CHARS) return `Вместе с вопросом — ${count}`
  const over = length - REQUEST_MAX_CHARS
  // Сократить на всю добавку — значит не добавлять ничего: тогда о сокращении
  // говорить нечего, говорим, как уйти без неё.
  if (over >= addition) {
    return `Вопрос уже занимает почти все ${digits(REQUEST_MAX_CHARS)} знаков — добавка не влезет. Очисти поле — вопрос уйдёт с ответами на карточки.`
  }
  return `Вместе с вопросом — ${digits(length)} ${pluralize(length, SIGN)}, а уходит не больше ${digits(REQUEST_MAX_CHARS)}. Сократи добавку на ${digits(over)} ${pluralize(over, SIGN)}.`
}

/** Объявление о закрытой отправке, когда за предел вывела добавка к вопросу. */
export const ADDED_SEND_CLOSED = `Вместе с вопросом длиннее ${digits(REQUEST_MAX_CHARS)} знаков — отправка закрыта, сократи добавку.`
